import {
  Box,
  Chip,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { isNil, noop } from "lodash-es";
import { useMemo, useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { FeatureName } from "@/common/constants/featureName";
import { ArrayHelper } from "@/common/helpers/array";
import { RepairHelper } from "@/common/helpers/entity/repair";
import { RepairSpecHelper } from "@/common/helpers/entity/repairSpec";
import { renderIf } from "@/common/helpers/render/renderIf";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { enumService } from "@/common/services/enum";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { featureManager } from "@/config/features";
import { apiClient } from "@/core/api/ApiClient";
import { AppPermission, RepairSpecDto, RepairSpecGetPaginatedDto } from "@/core/api/generated/v1";

import AppBadge from "../../Badge/AppBadge";
import AppIconButton from "../../Button/AppIconButton";
import GeneralPriceSummaryInline from "../../PriceSummary/GeneralPriceSummaryInline";
import AppTypography from "../../Text/AppTypography";
import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "../components/BaseEntitySearchAutocomplete";
import RepairSpecCreateUpdateModal from "./CreateUpdate/RepairSpecCreateUpdateModal";
import {
  RepairSpecAutocompleteCustomFilters,
  RepairSpecAutocompleteCustomFiltersValues,
  computeCustomFilters,
} from "./RepairSpecAutocompleteCustomFiltersInput";
import { RepairSpecAutocompleteCustomFiltersInputPopover } from "./RepairSpecAutocompleteCustomFiltersInputPopover";
import RepairSpecCrossTenantSelectorPopover from "./RepairSpecCrossTenantSelectorPopover";
import RepairSpecDetalizationInline from "./RepairSpecDetalizationInline";
import { getRepairSpecInlineText } from "./RepairSpecInline";
import RepairSpecViewModal from "./View/RepairSpecViewModal";

const defaultDisplayProps = {
  viewInModal: true,
  editInModal: true,
  crossTenantSelector: true,
  customFilters: true,
};

type RepairSpecAutocompleteOption = BaseAutocompleteOption<RepairSpecDto>;

export const getRepairSpecTitle = (data: RepairSpecDto): string | undefined =>
  getRepairSpecInlineText(data);

const repairSpecToOption = (data: RepairSpecDto): RepairSpecAutocompleteOption => ({
  id: data.id!,
  title: getRepairSpecTitle(data) || "Repair spec",
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface RepairSpecAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<RepairSpecDto> {
  searchFilters?: Partial<Omit<RepairSpecGetPaginatedDto, "search" | "includeIds">>;
  displayProps?: Partial<typeof defaultDisplayProps>;
  /** Custom filters can be changed by user. */
  defaultCustomFilters?: RepairSpecAutocompleteCustomFiltersValues;
}

export default function RepairSpecAutocomplete({
  searchFilters,
  displayProps,
  defaultCustomFilters,

  isPreload = true,
  onLoaded,
  ...otherProps
}: RepairSpecAutocompleteProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const currentTenant = useCurrentTenant();
  const authorizationInfo = useAuthorizationInfo();

  const initialFiltersEnablement = useMemo(
    () => RepairSpecHelper.getAutocompleteSettings()?.customFilters?.enablementMap,
    [],
  );
  const [customFilters, setCustomFilters] = useState<RepairSpecAutocompleteCustomFilters | null>(
    () =>
      defaultCustomFilters
        ? computeCustomFilters({
            enablementMap: initialFiltersEnablement,
            values: defaultCustomFilters,
          })
        : null,
  );
  const [isCustomFiltersChanged, setIsCustomFiltersChanged] = useState(false);

  // set default filters (when changed) if local filters not set yet
  if (defaultCustomFilters && (!customFilters || !customFilters.isAny)) {
    const newCustomFilters = computeCustomFilters({
      enablementMap: initialFiltersEnablement,
      values: defaultCustomFilters,
    });
    if (newCustomFilters.isAny) {
      setCustomFilters(newCustomFilters);
    }
  }

  // console.log("RepairSpecAutocomplete", {
  //   searchFilters,
  //   autocompleteSettings: RepairSpecHelper.getAutocompleteSettings(),
  //   initialFiltersEnablement,
  //   defaultCustomFilters,
  //   customFilters,
  //   isCustomFiltersChanged,
  // });

  return (
    <>
      <BaseEntitySearchAutocomplete
        sx={{ minWidth: 200, flex: 1 }}
        entityToOption={repairSpecToOption}
        isPreload={isPreload}
        isIgnoreCanLoadCondition={isCustomFiltersChanged} // hack to ensure refetch on custom filters change
        onLoaded={(entities) => {
          setIsCustomFiltersChanged(false);
          onLoaded && onLoaded(entities);
        }}
        request={{
          requestFunc: apiClient.repairSpecsApi.apiV1RepairSpecsGetPost,
          limit: 25,
          parameters: {
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            repairSpecGetPaginatedDto: {
              ...searchFilters,

              // prefer predefined filters over custom user filters
              partTypeId: searchFilters?.partTypeId || customFilters?.valuesEnabled?.partTypeId,
              damageTypeId:
                searchFilters?.damageTypeId || customFilters?.valuesEnabled?.damageTypeId,
              vehicleSize: searchFilters?.vehicleSize || customFilters?.valuesEnabled?.vehicleSize,
              bodyType: searchFilters?.bodyType || customFilters?.valuesEnabled?.bodyType,
              fuelType: searchFilters?.fuelType || customFilters?.valuesEnabled?.fuelType,
              makeId: searchFilters?.makeId || customFilters?.valuesEnabled?.makeId,
              modelId: searchFilters?.modelId || customFilters?.valuesEnabled?.modelId,
              generationId:
                searchFilters?.generationId || customFilters?.valuesEnabled?.generationId,
              modificationId:
                searchFilters?.modificationId || customFilters?.valuesEnabled?.modificationId,
              ids: ArrayHelper.undefinedIfEmpty([
                ...(searchFilters?.ids || []),
                ...(customFilters?.valuesEnabled?.suggestedIds || []),
                ...(customFilters?.valuesEnabled?.restrictedIds || []),
              ]),
            },
          },
          combineParameters: (params, newParams) => ({
            ...params,
            repairSpecGetPaginatedDto: {
              ...params.repairSpecGetPaginatedDto,
              ...newParams,
            },
          }),
          deps: [searchFilters, customFilters],
        }}
        label='Repair spec'
        placeholder='Search for repair spec...'
        renderOption={(props, option) => {
          const isBold = option.data?.matchMeta?.isPerfect ?? false;
          return (
            <ListItem {...props}>
              <ListItemIcon>
                {renderIf()
                  .if(option.optionType === AutocompleteOptionType.Normal)
                  .then(<AppIcon of='repairSpec' />)
                  .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                  .then(<AppIcon of='add' />)
                  .render()}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    sx={{ fontWeight: isBold ? "bold" : undefined }}
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    <Stack direction='row' justifyContent='space-between' alignItems='center'>
                      <Box>{option.data?.name || option.title}</Box>

                      {!isNil(option.data?.matchMeta?.score) && (
                        <Box>
                          <Tooltip
                            title='The bigger the match score the better the result suits your request.'
                            enterDelay={500}
                            enterNextDelay={500}
                          >
                            <Typography
                              sx={{ fontWeight: isBold ? "bold" : undefined }}
                              variant='caption'
                            >
                              <Chip
                                size='extraSmall'
                                variant='filled'
                                label={<>Match: {option.data?.matchMeta?.score} / 100</>}
                              />
                            </Typography>
                          </Tooltip>
                        </Box>
                      )}
                    </Stack>
                  </Typography>
                }
                secondary={
                  option.data && (
                    <AppTypography
                      sx={{ fontWeight: isBold ? "bold" : undefined }}
                      component='div'
                      variant='body2'
                    >
                      <AppTypography
                        tooltipProps={{
                          title: `${option.data?.partType?.name} - ${option.data?.damageType?.name}`,
                          enterDelay: 500,
                          enterNextDelay: 500,
                        }}
                        ellipsing={{ enabled: true }}
                        component='div'
                        isInheritFontStyle
                      >
                        {option.data?.partType?.name} - {option.data?.damageType?.name}
                      </AppTypography>

                      {option.data.detalization && (
                        <AppTypography
                          tooltipProps={{
                            title: (
                              <RepairSpecDetalizationInline
                                detalization={option.data.detalization}
                              />
                            ),
                            enterDelay: 500,
                            enterNextDelay: 500,
                          }}
                          ellipsing={{ enabled: true }}
                          component='div'
                          isInheritFontStyle
                        >
                          <RepairSpecDetalizationInline detalization={option.data.detalization} />
                        </AppTypography>
                      )}

                      <AppTypography
                        tooltipProps={{
                          title: `${enumService.getEnumValueName(
                            "RepairType",
                            option.data?.repairType,
                          )} repair`,
                          enterDelay: 500,
                          enterNextDelay: 500,
                        }}
                        ellipsing={{ enabled: true }}
                        component='div'
                        isInheritFontStyle
                      >
                        {`${enumService.getEnumValueName(
                          "RepairType",
                          option.data?.repairType,
                        )} repair`}
                      </AppTypography>

                      {option.data?.description && (
                        <AppTypography
                          tooltipProps={{
                            title: option.data?.description,
                            enterDelay: 500,
                            enterNextDelay: 500,
                          }}
                          ellipsing={{ enabled: true }}
                          component='div'
                          isInheritFontStyle
                        >
                          {option.data?.description}
                        </AppTypography>
                      )}

                      <AppTypography
                        tooltipProps={{
                          title: (
                            <AppTypography component='div' variant='body2'>
                              <GeneralPriceSummaryInline
                                sx={{
                                  "& .MuiTypography-caption": { color: "text.contrastText" },
                                  "& sub": { color: "text.contrastText" },
                                }}
                                summary={{
                                  currency: option.data?.currency,
                                  subTotal: option.data?.subTotal,
                                  discount: option.data?.discount,
                                  tax: option.data?.tax,
                                  total: option.data?.total,
                                }}
                              />
                            </AppTypography>
                          ),
                          enterDelay: 500,
                          enterNextDelay: 500,
                        }}
                        ellipsing={{ enabled: true }}
                        component='div'
                        isInheritFontStyle
                      >
                        <GeneralPriceSummaryInline
                          summary={{
                            currency: option.data?.currency,
                            subTotal: option.data?.subTotal,
                            discount: option.data?.discount,
                            tax: option.data?.tax,
                            total: option.data?.total,
                          }}
                          typographyProps={{
                            fontWeight: isBold ? "bold" : undefined,
                          }}
                        />
                      </AppTypography>

                      {RepairHelper.isExternal({
                        tenantId: currentTenant?.id,
                        repairEntity: option.data,
                      }) && (
                        <Box>
                          <Chip
                            variant='outlined'
                            size='extraSmall'
                            label={
                              <>
                                <AppIcon of='external' inText /> External
                              </>
                            }
                          />
                        </Box>
                      )}
                    </AppTypography>
                  )
                }
              />
            </ListItem>
          );
        }}
        entityViewModal={{
          if:
            displayProps?.viewInModal &&
            authorizationInfo.hasPermissions([AppPermission.RepairCatalogRead]),
          renderModal: (params) => (
            <RepairSpecViewModal
              {...params.dialogProps}
              viewProps={{
                repairSpec: params.entity,
                onDelete: null,
              }}
            />
          ),
        }}
        entityEditModal={{
          if:
            displayProps?.editInModal &&
            authorizationInfo.hasPermissions([AppPermission.RepairCatalogManage]),
          renderModal: (params) => (
            <RepairSpecCreateUpdateModal
              {...params.dialogProps}
              createUpdateProps={{
                repairSpecId: params.entity.id,
                defaultValues: {
                  data: params.entity,
                },
                onSave: (newValue) => {
                  params.handleEntityUpdated(newValue);
                  params.dialogProps?.onClose?.({}, "escapeKeyDown");
                },
              }}
            />
          ),
        }}
        endAdornmentActions={{
          actions: [
            {
              if:
                displayProps?.crossTenantSelector &&
                featureManager.isEnabled(FeatureName.CrossTenantRepairEntityAccess),
              renderAction: (params) => (
                <RepairSpecCrossTenantSelectorPopover
                  clickBehavior={{}}
                  trigger={
                    <AppIconButton
                      {...params.iconButtonProps}
                      title='My cross-company repair specs'
                    >
                      <AppIcon of='crossTenantBrowse' />
                    </AppIconButton>
                  }
                  selectorProps={{
                    repairSpecAutocompleteProps: {
                      searchFilters: { match: searchFilters?.match },
                    },
                    onSave: (newValue) => {
                      console.log("Cross-tenant repair spec selected", newValue);
                      if (newValue?.repairEntity) {
                        params.handleEntitySelected(newValue?.repairEntity);
                      }
                    },
                    onCancel: () => {},
                  }}
                />
              ),
            },
            {
              if: displayProps?.customFilters,
              renderAction: (params) => (
                <RepairSpecAutocompleteCustomFiltersInputPopover
                  clickBehavior={{}}
                  trigger={
                    <AppIconButton {...params.iconButtonProps} color='default' title='Filters'>
                      <AppIcon of='filter' />
                      {customFilters?.isAnyEnabled && (
                        <AppBadge
                          sx={{ position: "absolute", top: 0, right: 0 }}
                          color='secondary'
                          overlap='circular'
                          badgeContent={customFilters?.countEnabled}
                        />
                      )}
                    </AppIconButton>
                  }
                  subProps={{
                    searchFilters: searchFilters,
                    defaultFilters: customFilters?.values,
                    defaultEnablementMap: customFilters?.enablementMap,
                    onSave: (newValue) => {
                      setIsCustomFiltersChanged(true);
                      setCustomFilters(newValue);
                      RepairSpecHelper.saveAutocompleteCustomFilters(newValue);
                    },
                    onCancel: noop,
                  }}
                />
              ),
            },
          ],
        }}
        {...otherProps}
      />
    </>
  );
}
